import { useHead } from '@unhead/vue'
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { useRoute } from 'vue-router'
import { SITE_ORIGIN, DEFAULT_SITE_BRAND } from '@/utils/seo'
import { useSiteConfigStore } from '@/stores/site-config-store'

/** JSON-LD 结构化数据对象（schema.org） */
export type JsonLd = Record<string, unknown>

/**
 * 站点名（品牌名）响应式获取：取后台配置的 site_name（来自 /api/v1/site-config），
 * 未返回时回退到 DEFAULT_SITE_BRAND。SEO 文案与 JSON-LD 的站点名一律用它，禁止写死。
 * 返回 computed，配合 useSeo 的 getter 写法可在接口返回后自动更新（见 lottery/plans #238）。
 */
export function useSiteBrand(): ComputedRef<string> {
  const siteConfigStore = useSiteConfigStore()
  return computed(() => siteConfigStore.siteName || DEFAULT_SITE_BRAND)
}

export interface SeoOptions {
  /** 页面标题（已含品牌后缀，品牌名用 useSiteBrand()）。每页唯一，控制在 ~30 字内 */
  title?: MaybeRefOrGetter<string | undefined>
  /** 页面描述，~70–150 字，自然语句、含真实数据，每页唯一 */
  description?: MaybeRefOrGetter<string | undefined>
  /** 关键词，逗号分隔 */
  keywords?: MaybeRefOrGetter<string | undefined>
  /** og:type，默认 website；文章/详情类可传 article */
  ogType?: MaybeRefOrGetter<string | undefined>
  /**
   * JSON-LD 结构化数据。支持单个对象或数组（多段会各自输出一个 script 标签）。
   * 所有数字/时间必须是该页真实数据，禁止占位。
   */
  jsonLd?: MaybeRefOrGetter<JsonLd | JsonLd[] | undefined>
}

/** 拼接当前页的绝对 URL（用于 og:url / canonical） */
function buildAbsoluteUrl(path: string): string | undefined {
  if (!SITE_ORIGIN) return undefined
  return SITE_ORIGIN + path
}

/**
 * 页面 SEO 统一入口：随路由动态设置 title / description / keywords / og / canonical /
 * JSON-LD。基于 @unhead/vue，传入的 MaybeRefOrGetter 会保持响应式（异步数据就绪后自动更新）。
 *
 * 约定（与后端兜底配合）：前端配了的页面，后端检测到对应标签后不再注入兜底文案。
 * 因此本 composable 输出越完整，SEO 质量越高、越不会与后端冲突。
 *
 * @example
 * const brand = useSiteBrand()
 * useSeo({
 *   title: () => `${brand.value} - 澳门六合彩开奖结果…`,
 *   description: () => `${brand.value}提供澳门六合彩…`,
 *   jsonLd: () => siteJsonLd(brand.value),
 * })
 */
export function useSeo(options: SeoOptions): void {
  const route = useRoute()

  useHead(
    computed(() => {
      const title = toValue(options.title)
      const description = toValue(options.description)
      const keywords = toValue(options.keywords)
      const ogType = toValue(options.ogType) ?? 'website'
      const url = buildAbsoluteUrl(route.fullPath)

      const meta: { name?: string; property?: string; content: string }[] = []
      if (description) {
        meta.push(
          { name: 'description', content: description },
          { property: 'og:description', content: description },
        )
      }
      if (keywords) meta.push({ name: 'keywords', content: keywords })
      if (title) meta.push({ property: 'og:title', content: title })
      meta.push({ property: 'og:type', content: ogType })
      if (url) meta.push({ property: 'og:url', content: url })

      const link = url ? [{ rel: 'canonical' as const, href: url }] : []

      const raw = toValue(options.jsonLd)
      // 单个对象包成数组；空值由 filter(Boolean) 去掉
      const ldList = (Array.isArray(raw) ? raw : [raw]).filter(Boolean)
      const script = ldList.map((ld) => ({
        type: 'application/ld+json',
        // 用紧凑 JSON，避免占位/转义问题；内容由各页传入的真实数据生成
        innerHTML: JSON.stringify(ld),
      }))

      return {
        title: title || undefined,
        meta,
        link,
        script,
      }
    }),
  )
}
